import React, { useEffect, useState } from 'react';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Box, 
  IconButton, 
  InputAdornment, 
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CodeIcon from '@mui/icons-material/Code';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import apiService from '../services/api';

interface Mapping {
  id: string;
  diagnosisTerm: string;
  icdCodeId: string;
  icdCode: {
    id: string;
    icdCode: string;
    shortDescription: string;
    longDescription: string;
    codeType: string;
    category: string;
  };
  createdAt: string;
}

interface IcdCode {
  id: string;
  icdCode: string;
  shortDescription: string;
  longDescription: string;
  codeType: string;
  category: string;
}

const defaultForm: Partial<Mapping> = { 
  diagnosisTerm: '', 
  icdCodeId: '' 
};

const DiagnosisCodeMapping: React.FC = () => {
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [icdCodes, setIcdCodes] = useState<IcdCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState<Partial<Mapping>>(defaultForm);
  const [editing, setEditing] = useState<Mapping | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const data = await apiService.getDiagnosisTermsIcdCodeMapping(1, 1000);
      // Ensure data is an array before setting it
      if (Array.isArray(data)) {
        setMappings(data);
      } else {
        console.error('Expected array but received:', data);
        setMappings([]);
      }
    } catch (error) {
      console.error('Error fetching diagnosis terms ICD code mapping:', error);
      setMappings([]);
    }
    setLoading(false);
  };

  const fetchIcdCodes = async () => {
    try {
      const data = await apiService.getIcdCodes();
      // Handle the response format from the codes endpoint
      if (data && data.icdCodes && Array.isArray(data.icdCodes)) {
        setIcdCodes(data.icdCodes);
      } else if (Array.isArray(data)) {
        setIcdCodes(data);
      } else {
        console.error('Expected array but received:', data);
        setIcdCodes([]);
      }
    } catch (error) {
      console.error('Error fetching ICD codes:', error);
      setIcdCodes([]);
    }
  };

  useEffect(() => { 
    fetchMappings(); 
    fetchIcdCodes(); 
  }, []);

  const handleOpenDialog = () => {
    setForm(defaultForm);
    setEditing(null);
    setOpenDialog(true);
  };
  const handleEdit = (row: Mapping) => {
    setForm({
      diagnosisTerm: row.diagnosisTerm,
      icdCodeId: row.icdCodeId
    });
    setEditing(row);
    setOpenDialog(true);
  };
  const handleCloseDialog = () => { setOpenDialog(false); setForm(defaultForm); };
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };
  const handleSave = async () => {
    try {
      if (editing) {
        await apiService.updateDiagnosisTermsIcdCodeMapping(editing.id, form);
      } else {
        await apiService.createDiagnosisTermsIcdCodeMapping(form);
      }
      handleCloseDialog();
      fetchMappings();
    } catch (error) {
      console.error('Error saving diagnosis terms ICD code mapping:', error);
    }
  };
  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this mapping?')) return;
    try {
      await apiService.deleteDiagnosisTermsIcdCodeMapping(id);
      fetchMappings();
    } catch (error) {
      console.error('Error deleting diagnosis terms ICD code mapping:', error);
    }
  };

  const filteredMappings = (Array.isArray(mappings) ? mappings : []).filter(mapping =>
    mapping.diagnosisTerm.toLowerCase().includes(search.toLowerCase()) ||
    (mapping.icdCode?.icdCode || '').toLowerCase().includes(search.toLowerCase()) ||
    (mapping.icdCode?.shortDescription || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearch('');
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Paginate the filtered mappings
  const paginatedMappings = filteredMappings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a237e' }}>
            Diagnosis Code Mapping
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Map diagnosis terms to ICD-10 codes
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
            }
          }}
        >
          Add Mapping
        </Button>
      </Box>

      {/* Search Box */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by diagnosis term, ICD code, or description..."
            value={search}
            onChange={handleSearchChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#1976d2',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1976d2',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#666' }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    edge="end"
                    sx={{ color: '#666' }}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Mappings Grid */}
      <Card sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Diagnosis Term
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  ICD Code
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  ICD Description
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Category
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        Loading mappings...
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please wait while we fetch the data
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : paginatedMappings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <LinkIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        {search ? 'No mappings found' : 'No mappings available'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {search 
                          ? 'Try adjusting your search terms or clear the search to see all mappings.'
                          : 'Get started by adding your first diagnosis code mapping.'
                        }
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedMappings.map((mapping) => (
                  <TableRow 
                    key={mapping.id}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: '#f8f9fa',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease-in-out'
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: '#fafafa'
                      }
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <DescriptionIcon sx={{ fontSize: 16, color: '#666' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {mapping.diagnosisTerm}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={mapping.icdCode?.icdCode || 'N/A'} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {mapping.icdCode?.shortDescription || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CategoryIcon sx={{ fontSize: 16, color: '#666' }} />
                        <Typography variant="body2">
                          {mapping.icdCode?.category || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <IconButton 
                          onClick={() => handleEdit(mapping)}
                          size="small"
                          sx={{ 
                            color: '#1976d2',
                            '&:hover': { 
                              backgroundColor: 'rgba(25, 118, 210, 0.1)',
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDelete(mapping.id)}
                          size="small"
                          sx={{ 
                            color: '#d32f2f',
                            '&:hover': { 
                              backgroundColor: 'rgba(211, 47, 47, 0.1)',
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Box sx={{ borderTop: '1px solid #e0e0e0' }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredMappings.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontWeight: 500,
                color: '#666'
              }
            }}
          />
        </Box>
      </Card>

      {/* Mapping Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
          color: 'white',
          fontWeight: 600
        }}>
          {editing ? 'Edit Mapping' : 'Add New Mapping'}
        </DialogTitle>
        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Diagnosis Term"
                  name="diagnosisTerm"
                  value={form.diagnosisTerm || ''}
                  onChange={handleFormChange}
                  required
                  multiline
                  rows={3}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={icdCodes}
                  getOptionLabel={(option) => `${option.icdCode} - ${option.shortDescription}`}
                  value={icdCodes.find(code => code.id === form.icdCodeId) || null}
                  onChange={(event, newValue) => {
                    setForm(f => ({ ...f, icdCodeId: newValue?.id || '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="ICD Code"
                      fullWidth
                      required
                      sx={{ mb: 2 }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <strong>{option.icdCode}</strong> - {option.shortDescription}
                      </Box>
                    </Box>
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button onClick={handleCloseDialog} sx={{ color: '#666' }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained"
              sx={{
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                }
              }}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};
export default DiagnosisCodeMapping; 